/**
 * Fetch Popular Categories API
 *
 * API function for fetching popular/featured categories
 */

import { buildApiUrl, handleApiResponse } from '@/src/shared/api';
import type { Category, GetPopularCategoriesResponse } from '../types';

export interface FetchPopularCategoriesParams {
  limit?: number;
  useMock?: boolean;
}

/**
 * Fetch popular/featured categories from the API
 *
 * @param params - Optional parameters for limiting results and mock mode
 * @returns Promise with array of popular categories
 */
export async function fetchPopularCategories(
  params: FetchPopularCategoriesParams = {},
): Promise<Category[]> {
  const { limit = 4, useMock = false } = params;

  // Use mock data for development
  if (useMock) {
    const { mockCategories } = await import('../model/mock/mock-categories');
    return new Promise((resolve) => {
      setTimeout(() => {
        // Return first N popular root categories
        const popularCategories = mockCategories
          .filter((cat) => cat.parentId === null && cat.isPopular)
          .slice(0, limit);

        resolve(popularCategories);
      }, 800);
    });
  }

  // Real API call
  const url = buildApiUrl(`/categories/popular?limit=${limit}`);

  const response = await fetch(url);
  const data = await handleApiResponse<GetPopularCategoriesResponse>(response);

  // Backend returns PopularCategoryItem[], map to Category[] for MVP
  // The _count field is optional and can be ignored by consumers
  return Array.isArray(data) ? data : [];
}
